/**
 * 기준정보 — 기관계좌·기관카드
 * - 탭: 기관계좌 | 기관카드 (각 단일 그리드)
 * - 계좌: 부서·사업 각각 N건 멀티(외 N건 + 호버) · null=제한 없음
 * - 카드: 연결계좌 팝업 · 부서/사업은 연결 계좌 조회전용
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { ModeTabBar, ModeTab } from 'components/system/SystemPageChrome.styles';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { showAlert, showConfirm } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import {
  ACCOUNT_PURPOSE_OPTIONS,
  CARD_TYPE_OPTIONS,
  CARD_TYPE,
  CARD_USAGE_OPTIONS,
  CARD_USAGE,
  ACCOUNT_PURPOSE,
  YN_OPTIONS,
  emptyAccount,
  emptyCard,
  enrichAccountRow,
  enrichCardRow,
  filterOrgAccounts,
  filterOrgCards,
  loadOrgAccounts,
  loadOrgCards,
  saveOrgFinanceBundle,
  getCachedOrgAccounts,
  ensureOrgFinanceSeed,
  validateOrgFinanceBundle,
  createAccountUid,
  createCardUid,
  nextAccountCode,
  nextCardCode,
  nextSortOrderBy10,
  normalizeAccountNo,
  formatCardNo,
  normalizeCardNo,
  normalizeCodeNameLists,
  maskAccountNo,
  summarizeMultiNames,
  multiSelectionTooltip,
  downloadOrgFinanceExcelTemplate,
  parseOrgFinanceExcelWorkbook,
  mergeUploadedOrgFinance,
} from 'master/orgFinanceStore';

const ACCOUNT_GRID_ID = 'master-org-account';
const CARD_GRID_ID = 'master-org-card';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
  flex-shrink: 0;
`;

const TabPanel = styled.div`
  display: ${(p) => (p.$show ? 'flex' : 'none')};
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

function OrgFinanceRegisterInner() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState('account');
  const modeRef = useRef('account');
  modeRef.current = mode;

  const { handleActiveAdd, handleActiveDelete, activeLabel } = useActiveGridCrudActions({
    guardAdd: (id) => {
      const m = modeRef.current;
      if (m === 'account' && id === CARD_GRID_ID) return '기관계좌 탭에서 추가하세요.';
      if (m === 'card' && id === ACCOUNT_GRID_ID) return '기관카드 탭에서 추가하세요.';
      return null;
    },
  });

  const accountRef = useRef(null);
  const cardRef = useRef(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [hint, setHint] = useState('기관계좌·기관카드를 조회하세요.');
  const [lookup, setLookup] = useState({
    open: false,
    type: '',
    accountUid: '',
    cardUid: '',
  });

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      keyword: '',
      purpose: '',
      cardType: '',
      cardUsage: '',
      useYn: 'Y',
      fiscalYear: getCurrentFiscalYear(),
    },
  });
  const { getValues } = methods;

  const patchAccountRow = useCallback((accountUid, patch) => {
    if (!accountUid || !patch) return;
    const data = accountRef.current?.getData?.() || [];
    accountRef.current?.setData?.(
      data.map((r) => {
        if (r.accountUid !== accountUid) return r;
        const next = { ...r, ...patch, mod: r.mod === 'A' ? 'A' : 'U' };
        return enrichAccountRow(next);
      })
    );
  }, []);

  const patchCardRow = useCallback(
    (cardUid, patch) => {
      if (!cardUid || !patch) return;
      const accounts =
        allAccounts.length > 0
          ? allAccounts
          : (accountRef.current?.getData?.() || []).concat(getCachedOrgAccounts(tenantCode));
      const data = cardRef.current?.getData?.() || [];
      cardRef.current?.setData?.(
        data.map((r) => {
          if (r.cardUid !== cardUid) return r;
          return enrichCardRow({ ...r, ...patch, mod: r.mod === 'A' ? 'A' : 'U' }, accounts);
        })
      );
    },
    [allAccounts, tenantCode]
  );

  const openDeptLookup = useCallback((row) => {
    if (!row?.accountUid) {
      showAlert('계좌 행을 선택하세요.');
      return;
    }
    setLookup({ open: true, type: 'dept', accountUid: row.accountUid });
  }, []);

  const openProjectLookup = useCallback((row) => {
    if (!row?.accountUid) {
      showAlert('계좌 행을 선택하세요.');
      return;
    }
    setLookup({ open: true, type: 'project', accountUid: row.accountUid });
  }, []);

  const openAccountLookupForCard = useCallback((row) => {
    if (!row?.cardUid) {
      showAlert('카드 행을 선택하세요.');
      return;
    }
    setLookup({ open: true, type: 'account', cardUid: row.cardUid });
  }, []);

  const accountColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '계좌코드', field: 'accountCode', size: 90 }),
      hiCol.edTextColumn({ header: '계좌명', field: 'accountName', size: 140, required: true }),
      hiCol.edComboColumn({
        header: '용도',
        field: 'purpose',
        size: 90,
        options: ACCOUNT_PURPOSE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({ header: '은행코드', field: 'bankCode', size: 80 }),
      hiCol.edTextColumn({ header: '은행명', field: 'bankName', size: 90 }),
      hiCol.edTextColumn({ header: '계좌번호', field: 'accountNo', size: 140, required: true }),
      hiCol.edTextColumn({ header: '예금주', field: 'accountHolder', size: 100 }),
      hiCol.edTextColumn({
        header: '부서',
        field: 'deptDisp',
        size: 140,
        showSearch: true,
        searchTitle: '부서 조회 (복수 · 조건 해제=제한 없음)',
        onSearch: ({ row }) => openDeptLookup(row),
        title: '복수 선택 · 「외 N건」 · 빈값(조건 해제)=부서 제한 없음',
      }),
      hiCol.edTextColumn({
        header: '사업',
        field: 'projectDisp',
        size: 160,
        showSearch: true,
        searchTitle: '사업 조회 (복수 · 조건 해제=제한 없음)',
        onSearch: ({ row }) => openProjectLookup(row),
        title: '복수 선택 · 「외 N건」 · 빈값(조건 해제)=사업 제한 없음',
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 70, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 120 }),
    ],
    [openDeptLookup, openProjectLookup]
  );

  const cardColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '카드코드', field: 'cardCode', size: 90 }),
      hiCol.edTextColumn({ header: '카드명', field: 'cardName', size: 120, required: true }),
      hiCol.edComboColumn({
        header: '카드종류',
        field: 'cardType',
        size: 95,
        options: CARD_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '용도',
        field: 'cardUsage',
        size: 90,
        options: CARD_USAGE_OPTIONS,
        required: true,
        title: '결의 시 승인내역 그룹 필터 · 추후 공통코드(ORG_CARD_USAGE)',
      }),
      hiCol.edTextColumn({
        header: '카드번호',
        field: 'cardNo',
        size: 160,
        required: true,
        title: '화면 하이픈 표시 · 저장은 숫자 16자리',
      }),
      hiCol.edTextColumn({
        header: '유효기간',
        field: 'expireText',
        size: 90,
        title: '단순 텍스트 (예: 12/28)',
      }),
      hiCol.edTextColumn({
        header: '연결계좌명',
        field: 'accountName',
        size: 130,
        required: true,
        showSearch: true,
        searchTitle: '기관계좌 조회',
        onSearch: ({ row }) => openAccountLookupForCard(row),
      }),
      hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 75 }),
      hiCol.roTextColumn({ header: '계좌번호', field: 'accountNoDisp', size: 110 }),
      hiCol.roTextColumn({
        header: '부서(계좌)',
        field: 'deptDisp',
        size: 110,
        title: '연결 계좌의 부서 (호버 시 목록)',
      }),
      hiCol.roTextColumn({
        header: '사업(계좌)',
        field: 'projectDisp',
        size: 120,
        title: '연결 계좌의 사업 (호버 시 목록)',
      }),
      hiCol.edTextColumn({ header: '카드사', field: 'issuerName', size: 85 }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 65, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 65, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 90 }),
    ],
    [openAccountLookupForCard]
  );

  const accountGrid = useHiGridHook({
    gridRef: accountRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyAccount({ purpose: ACCOUNT_PURPOSE.EXPENSE, useYn: 'Y' }),
  });

  const cardGrid = useHiGridHook({
    gridRef: cardRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyCard({
      cardType: CARD_TYPE.CREDIT,
      cardUsage: CARD_USAGE.PROJECT,
      useYn: 'Y',
    }),
  });

  // 카드번호: 셀 편집 후 화면용 하이픈 · F10 저장 시 숫자만 persist
  useEffect(() => {
    cardGrid.onAfterSaveCell = ({ data, field }) => {
      if (field !== 'cardNo' || !data) return;
      const formatted = formatCardNo(data.cardNo);
      if (formatted === String(data.cardNo || '')) return;
      const api = cardRef.current;
      const rows = api?.getData?.() || [];
      api?.setData?.(
        rows.map((r) => {
          if (r !== data && r.cardUid !== data.cardUid) return r;
          return {
            ...r,
            cardNo: formatted,
            mod: r.mod === 'A' ? 'A' : 'U',
          };
        })
      );
    };
  }, [cardGrid]);

  const handleSearch = useCallback(async () => {
    await ensureOrgFinanceSeed(tenantCode);
    const accounts = await loadOrgAccounts(tenantCode);
    const cards = await loadOrgCards(tenantCode);
    setAllAccounts(accounts);

    const q = getValues();
    const filteredAcc = filterOrgAccounts(accounts, q).map(enrichAccountRow);
    const filteredCard = filterOrgCards(cards, q).map((r) => enrichCardRow(r, accounts));

    accountRef.current?.setData?.(filteredAcc);
    cardRef.current?.setData?.(filteredCard);

    setHint(
      mode === 'account'
        ? `계좌 ${filteredAcc.length}건 · 부서/사업 ··· (빈값=제한 없음)`
        : `카드 ${filteredCard.length}건 · 부서·사업은 연결 계좌 기준`
    );
  }, [tenantCode, getValues, mode]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCode]);

  const handleModeChange = useCallback(
    (next) => {
      if (next === mode) return;
      setMode(next);
      setHint(
        next === 'account'
          ? '기관계좌 · 부서/사업 필드로 N건 매핑'
          : '기관카드 · 연결계좌의 부서·사업 확인'
      );
    },
    [mode]
  );

  const handleAccountAdd = useCallback(async () => {
    const data = accountRef.current?.getData?.() || [];
    await accountGrid.handleAdd(
      enrichAccountRow(
        emptyAccount({
          accountUid: createAccountUid(),
          accountCode: nextAccountCode(data),
          purpose: ACCOUNT_PURPOSE.EXPENSE,
          sortOrder: nextSortOrderBy10(data),
          useYn: 'Y',
          mod: 'A',
        })
      )
    );
  }, [accountGrid]);

  const handleCardAdd = useCallback(async () => {
    const data = cardRef.current?.getData?.() || [];
    await cardGrid.handleAdd(
      enrichCardRow(
        emptyCard({
          cardUid: createCardUid(),
          cardCode: nextCardCode(data),
          cardType: CARD_TYPE.CREDIT,
          cardUsage: CARD_USAGE.PROJECT,
          expireText: '',
          accountUid: '',
          sortOrder: nextSortOrderBy10(data),
          useYn: 'Y',
          mod: 'A',
        }),
        allAccounts
      )
    );
  }, [cardGrid, allAccounts]);

  const handleSave = useCallback(async () => {
    const gridAccounts = (accountRef.current?.getData?.() || []).map((r) => {
      const depts = normalizeCodeNameLists(r.deptCodes, r.deptNames);
      const projects = normalizeCodeNameLists(r.projectCodes, r.projectNames);
      return {
        ...r,
        accountUid: r.accountUid || createAccountUid(),
        accountNo: normalizeAccountNo(r.accountNo),
        deptCodes: depts.codes,
        deptNames: depts.names,
        projectCodes: projects.codes,
        projectNames: projects.names,
      };
    });
    const gridCards = (cardRef.current?.getData?.() || []).map((r) => ({
      ...r,
      cardUid: r.cardUid || createCardUid(),
      cardNo: normalizeCardNo(r.cardNo),
      cardUsage: r.cardUsage || CARD_USAGE.PROJECT,
      expireText: String(r.expireText || '').trim(),
    }));

    const storedAcc = await loadOrgAccounts(tenantCode);
    const storedCard = await loadOrgCards(tenantCode);
    const accIds = new Set(gridAccounts.map((a) => a.accountUid));
    const cardIds = new Set(gridCards.map((c) => c.cardUid));
    const accounts = [
      ...gridAccounts,
      ...storedAcc.filter((a) => !accIds.has(a.accountUid)),
    ];
    const cards = [...gridCards, ...storedCard.filter((c) => !cardIds.has(c.cardUid))];

    const errors = validateOrgFinanceBundle(accounts, cards);
    if (errors.length) {
      showAlert(errors.slice(0, 10).join('\n'));
      return;
    }

    await saveOrgFinanceBundle(tenantCode, { accounts, cards });
    setAllAccounts(accounts.filter((a) => a.mod !== 'D'));
    showAlert('저장되었습니다.');
    await handleSearch();
  }, [tenantCode, handleSearch]);

  const handleTemplateDownload = useCallback(() => {
    downloadOrgFinanceExcelTemplate('기관계좌카드업로드양식.xlsx');
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUploadFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const buf = await file.arrayBuffer();
        const parsed = parseOrgFinanceExcelWorkbook(buf);
        if (parsed.errors?.length && !parsed.accounts.length && !parsed.cards.length) {
          showAlert(parsed.errors.slice(0, 8).join('\n'));
          return;
        }
        if (!parsed.accounts.length && !parsed.cards.length) {
          showAlert('업로드할 계좌·카드가 없습니다.');
          return;
        }
        const ok = await showConfirm(
          `계좌 ${parsed.accounts.length}건 · 카드 ${parsed.cards.length}건을 반영할까요?\n(계좌 부서·사업 합집합 매핑 → 카드는 계좌 연결)`
        );
        if (!ok) return;

        await ensureOrgFinanceSeed(tenantCode);
        const merged = mergeUploadedOrgFinance(
          await loadOrgAccounts(tenantCode),
          await loadOrgCards(tenantCode),
          parsed
        );
        const errors = validateOrgFinanceBundle(merged.accounts, merged.cards);
        if (errors.length) {
          showAlert(`검증 실패\n${errors.slice(0, 8).join('\n')}`);
          return;
        }
        await saveOrgFinanceBundle(tenantCode, merged);
        showAlert(
          parsed.errors?.length
            ? `반영 완료 (경고)\n${parsed.errors.slice(0, 5).join('\n')}`
            : '업로드·저장이 완료되었습니다.'
        );
        await handleSearch();
      } catch (err) {
        showAlert(`엑셀 읽기 실패: ${err?.message || err}`);
      }
    },
    [tenantCode, handleSearch]
  );

  const lookupConfig = useMemo(() => {
    const fy = String(getValues('fiscalYear') || getCurrentFiscalYear()).trim();
    if (lookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenantCode), { useYn: 'Y' });
      return {
        title: '부서 조회 (복수 · 조건 해제=제한 없음)',
        rows: rows.map((r) => ({ ...r, id: r.deptUid || r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['deptCode', 'deptName'] },
        multiSelect: true,
      };
    }
    if (lookup.type === 'project') {
      const loaded = loadOrSeedProjects({ tenantCode, fiscalYear: fy }) || [];
      const rows = (Array.isArray(loaded) ? loaded : []).filter((r) => r.useYn !== 'N');
      return {
        title: '사업 조회 (복수 · 조건 해제=제한 없음)',
        rows: rows.map((r) => ({ ...r, id: r.projectUid || r.projectCode })),
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['projectCode', 'projectName'] },
        multiSelect: true,
      };
    }
    if (lookup.type === 'account') {
      const live =
        allAccounts.length > 0 ? allAccounts : getCachedOrgAccounts(tenantCode);
      const rows = live.filter((a) => a.mod !== 'D' && a.useYn !== 'N').map(enrichAccountRow);
      return {
        title: '기관계좌 조회',
        rows: rows.map((r) => ({ ...r, id: r.accountUid })),
        columns: [
          hiCol.roTextColumn({ header: '계좌코드', field: 'accountCode', size: 90 }),
          hiCol.roTextColumn({ header: '계좌명', field: 'accountName', size: 130 }),
          hiCol.roTextColumn({ header: '용도', field: 'purposeNm', size: 70 }),
          hiCol.roTextColumn({ header: '부서', field: 'deptDisp', size: 110 }),
          hiCol.roTextColumn({ header: '사업', field: 'projectDisp', size: 120 }),
          hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 80 }),
          hiCol.roTextColumn({ header: '계좌번호', field: 'accountNo', size: 130 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: [
            'accountCode',
            'accountName',
            'bankName',
            'accountNo',
            'deptDisp',
            'projectDisp',
          ],
        },
        multiSelect: false,
      };
    }
    return { title: '', rows: [], columns: [], enableSearch: false, multiSelect: false };
  }, [lookup.type, tenantCode, getValues, allAccounts]);

  const applyLookup = useCallback(
    (selected) => {
      const list = Array.isArray(selected) ? selected : selected ? [selected] : [];

      if (lookup.type === 'dept') {
        // 빈 배열 = 조건 해제 = 부서 제한 없음
        const codes = list.map((r) => r.deptCode || '');
        const names = list.map((r) => r.deptName || r.deptCode || '');
        const norm = normalizeCodeNameLists(codes, names);
        patchAccountRow(lookup.accountUid, {
          deptCodes: norm.codes,
          deptNames: norm.names,
          deptDisp: summarizeMultiNames(norm.names),
          deptTitle: multiSelectionTooltip(norm.codes, norm.names, '부서 제한 없음(전체)'),
        });
      } else if (lookup.type === 'project') {
        const codes = list.map((r) => r.projectCode || '');
        const names = list.map((r) => r.projectName || r.projectCode || '');
        const norm = normalizeCodeNameLists(codes, names);
        patchAccountRow(lookup.accountUid, {
          projectCodes: norm.codes,
          projectNames: norm.names,
          projectDisp: summarizeMultiNames(norm.names),
          projectTitle: multiSelectionTooltip(
            norm.codes,
            norm.names,
            '사업 제한 없음(전체)'
          ),
        });
      } else if (lookup.type === 'account') {
        if (!list.length) {
          setLookup({ open: false, type: '', accountUid: '', cardUid: '' });
          return;
        }
        const acc = list[0];
        patchCardRow(lookup.cardUid, {
          accountUid: acc.accountUid,
          accountName: acc.accountName || '',
          bankName: acc.bankName || '',
          accountNoDisp: maskAccountNo(acc.accountNo),
        });
      }

      setLookup({ open: false, type: '', accountUid: '', cardUid: '' });
    },
    [lookup, patchAccountRow, patchCardRow]
  );

  useRegisterGridExport({
    id: ACCOUNT_GRID_ID,
    label: '기관계좌',
    getData: () =>
      (accountRef.current?.getFilteredData?.() ?? accountRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    getDataColumns: () => accountRef.current?.getDataColumns?.() ?? accountColumns,
    joinKeys: ['accountUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: CARD_GRID_ID,
    label: '기관카드',
    getData: () =>
      (cardRef.current?.getFilteredData?.() ?? cardRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    getDataColumns: () => cardRef.current?.getDataColumns?.() ?? cardColumns,
    joinKeys: ['accountUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '기관계좌카드',
  });

  const searchFields = useMemo(() => {
    const base = [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: '88px',
        labelWidth: SEARCH_LABEL_W,
        title: '사업 조회 팝업 기준 연도',
      },
      {
        name: 'keyword',
        label: '검색어',
        type: 'text',
        width: '180px',
        labelWidth: SEARCH_LABEL_W,
        placeholder: mode === 'account' ? '계좌·부서·사업' : '카드명·번호',
      },
    ];
    if (mode === 'account') {
      base.push({
        name: 'purpose',
        label: '용도',
        type: 'select',
        options: [{ value: '', label: '전체' }, ...ACCOUNT_PURPOSE_OPTIONS],
        width: '120px',
        labelWidth: SEARCH_LABEL_W,
      });
    } else {
      base.push({
        name: 'cardType',
        label: '종류',
        type: 'select',
        options: [{ value: '', label: '전체' }, ...CARD_TYPE_OPTIONS],
        width: '110px',
        labelWidth: SEARCH_LABEL_W,
      });
      base.push({
        name: 'cardUsage',
        label: '용도',
        type: 'select',
        options: [{ value: '', label: '전체' }, ...CARD_USAGE_OPTIONS],
        width: '110px',
        labelWidth: SEARCH_LABEL_W,
        title: '결의 승인내역 그룹 필터용',
      });
    }
    base.push({
      name: 'useYn',
      label: '사용',
      type: 'select',
      options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
      width: '88px',
      labelWidth: SEARCH_LABEL_W,
    });
    return [base];
  }, [mode]);

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
      },
    ],
    [
      handleSearch,
      handleActiveAdd,
      handleActiveDelete,
      handleSave,
      openExcelDialog,
      activeLabel,
    ]
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'tpl',
        label: '양식다운로드',
        icon: '📄',
        onClick: handleTemplateDownload,
        title: '계좌(부서·사업 합집합) → 카드(계좌 연결) 2시트',
      },
      {
        id: 'upload',
        label: '엑셀업로드',
        icon: '📤',
        onClick: handleUploadClick,
      },
    ],
    [handleTemplateDownload, handleUploadClick]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="master-org-finance"
          prefsUserId={prefsUserId}
          title="조회조건"
        >
          <FormArea compact fields={searchFields} onFieldEnter={handleSearch} />
        </CollapsibleSearchPanel>
      </FormProvider>

      <ModeTabBar>
        <ModeTab
          type="button"
          $active={mode === 'account'}
          onClick={() => handleModeChange('account')}
        >
          기관계좌
        </ModeTab>
        <ModeTab type="button" $active={mode === 'card'} onClick={() => handleModeChange('card')}>
          기관카드
        </ModeTab>
      </ModeTabBar>

      <Meta>
        활성: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        부서/사업 빈값=제한 없음 · 사업만 매핑 시 해당 사업 접근 부서 집행 가능
        {' · '}
        {hint}
      </Meta>

      <Stack>
        <TabPanel $show={mode === 'account'}>
          <Panel>
            <ActiveGridHeader gridId={ACCOUNT_GRID_ID} extra="부서·사업 ··· 멀티 · null 허용">
              기관계좌
            </ActiveGridHeader>
            <GridBody>
              <ActiveGridScope
                gridId={ACCOUNT_GRID_ID}
                label="기관계좌"
                order={0}
                canAdd={mode === 'account'}
                canDelete={mode === 'account'}
                onAdd={handleAccountAdd}
                onDelete={accountGrid.handleDelete}
              >
                <GridExportFocusScope id={ACCOUNT_GRID_ID}>
                  <HiGridComponent
                    ref={accountRef}
                    columns={accountColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableContextMenu
                    enableRowEditMenu
                    enableCut
                    enableFindReplace
                    allowReplace
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    exportFileName="기관계좌"
                    gridId={ACCOUNT_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...accountGrid.contextMenuProps}
                  />
                </GridExportFocusScope>
              </ActiveGridScope>
            </GridBody>
          </Panel>
        </TabPanel>

        <TabPanel $show={mode === 'card'}>
          <Panel>
            <ActiveGridHeader
              gridId={CARD_GRID_ID}
              extra="부서·사업은 연결 계좌 조회"
            >
              기관카드
            </ActiveGridHeader>
            <GridBody>
              <ActiveGridScope
                gridId={CARD_GRID_ID}
                label="기관카드"
                order={1}
                canAdd={mode === 'card'}
                canDelete={mode === 'card'}
                onAdd={handleCardAdd}
                onDelete={cardGrid.handleDelete}
              >
                <GridExportFocusScope id={CARD_GRID_ID}>
                  <HiGridComponent
                    ref={cardRef}
                    columns={cardColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableContextMenu
                    enableRowEditMenu
                    enableCut
                    enableFindReplace
                    allowReplace
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    exportFileName="기관카드"
                    gridId={CARD_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...cardGrid.contextMenuProps}
                  />
                </GridExportFocusScope>
              </ActiveGridScope>
            </GridBody>
          </Panel>
        </TabPanel>
      </Stack>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        columns={lookupConfig.columns}
        rows={lookupConfig.rows}
        enableSearch={lookupConfig.enableSearch}
        searchConfig={lookupConfig.searchConfig}
        multiSelect={!!lookupConfig.multiSelect}
        prefsUserId={prefsUserId}
        sizeKey={`codeLookup-org-finance-${lookup.type || 'x'}`}
        onSelect={applyLookup}
        onClose={() => setLookup({ open: false, type: '', accountUid: '', cardUid: '' })}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleUploadFile}
      />

      {excelDialog}
    </PageContainer>
  );
}

export default function OrgFinanceRegister() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <OrgFinanceRegisterInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
